import { MemberTableDatatypes } from "@/types/member";

/**
 * Pricing rule arrays accepted by calculateItemPrice.
 * These mirror the shapes already used across facility-slots, event-card, and
 * the PriceMaster type.  Everything is optional so callers can pass only what
 * they have.
 */
export interface SpecialPricing {
  name: string;
  price: number;
  date_from: string;
  date_to: string;
}

export interface PlanPricing {
  group_id?: number;
  membership_ids?: (number | string)[];
  membership_plan_ids?: number[];   // PriceMaster uses this key
  price: number;
}

export interface GroupPricing {
  group_id: number | string;
  price: number;
}

export interface ItemPriceResult {
  price: number;
  priceType: "base" | "special" | "plan" | "group";
  appliedPricingName: string;
}

/**
 * Resolve the base (regular) price for any item type.
 *
 * Priority:
 *  1. If a `priceMasterId` is provided and exists in `priceMap`, use that price.
 *  2. Otherwise fall back to `fallbackPrice`.
 */
/**
 * Unified item-price calculator.
 *
 * Works identically for facilities, events, products and memberships:
 *   1. Start with `basePrice` (already resolved via `resolveBasePrice`).
 *   2. Check special pricing (date-range match against `referenceDate`).
 *   3. Check plan pricing (member's active membership plan IDs).
 *   4. Check group pricing (member's group ID).
 *   5. Return the **lowest** applicable price.
 */
export const calculateItemPrice = (
  basePrice: number,
  options?: {
    specialPricing?: SpecialPricing[];
    planPricing?: PlanPricing[];
    groupPricing?: GroupPricing[];
    member?: MemberTableDatatypes | null;
    /** Date used for special-pricing date-range checks.  Defaults to today. */
    referenceDate?: Date | string;
  },
): ItemPriceResult => {
  const {
    specialPricing = [],
    planPricing = [],
    groupPricing = [],
    member = null,
    referenceDate,
  } = options ?? {};

  // Collect all candidate prices
  const prices: Array<{
    price: number;
    type: "base" | "special" | "plan" | "group";
    name: string;
  }> = [{ price: basePrice, type: "base", name: "" }];

  // --- Special Pricing (date-range based) ---
  if (specialPricing.length > 0) {
    const refDate = referenceDate
      ? typeof referenceDate === "string"
        ? referenceDate.split("T")[0]
        : referenceDate.toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0];

    const applicable = specialPricing.filter(
      (sp) =>
        sp.date_from &&
        sp.date_to &&
        refDate >= sp.date_from.split("T")[0] &&
        refDate <= sp.date_to.split("T")[0],
    );

    if (applicable.length > 0) {
      const lowest = applicable.reduce((min, curr) =>
        curr.price < min.price ? curr : min,
      );
      prices.push({
        price: lowest.price,
        type: "special",
        name: lowest.name || "Special",
      });
    }
  }

  // --- Plan Pricing (member's membership plan IDs) ---
  if (member && planPricing.length > 0) {
    const memberPlans = member.membership_plans || [];
    if (memberPlans.length > 0) {
      const customerPlanIds = memberPlans.map((mp) => mp.membership_plan_id);

      const applicable = planPricing.filter((pp) => {
        // Support both `membership_ids` (schedule/event shape) and
        // `membership_plan_ids` (PriceMaster shape)
        const ids = pp.membership_ids ?? pp.membership_plan_ids ?? [];
        return ids.some((id) => customerPlanIds.includes(Number(id)));
      });

      if (applicable.length > 0) {
        const lowest = applicable.reduce((min, curr) =>
          curr.price < min.price ? curr : min,
        );

        // Find which plan matched for display
        const bestPlan = memberPlans.find((mp) =>
          planPricing.some((pp) => {
            const ids = pp.membership_ids ?? pp.membership_plan_ids ?? [];
            return (
              pp.price === lowest.price &&
              ids.some((id) => Number(id) === mp.membership_plan_id)
            );
          }),
        );

        prices.push({
          price: lowest.price,
          type: "plan",
          name: bestPlan
            ? String(bestPlan.membership_plan_name || bestPlan.name)
            : "Plan",
        });
      }
    }
  }

  // --- Group Pricing (member's group ID) ---
  if (member?.member_group_id && groupPricing.length > 0) {
    const applicable = groupPricing.filter(
      (gp) => Number(gp.group_id) === Number(member.member_group_id),
    );

    if (applicable.length > 0) {
      const lowest = applicable.reduce((min, curr) =>
        curr.price < min.price ? curr : min,
      );
      prices.push({
        price: lowest.price,
        type: "group",
        name: "Group",
      });
    }
  }

  console.log(`this is prices ${JSON.stringify(prices)}`)

  // --- Pick the absolute lowest ---
  const best = prices.reduce((min, curr) =>
    curr.price < min.price ? curr : min,
  );

  return {
    price: best.price,
    priceType: best.type,
    appliedPricingName: best.name,
  };
};
